import { Box } from "@material-ui/core";
import React, { useCallback } from "react";
import { DataGridLocale } from "../../common/DataGridLocale";
import { InputType } from "../../common/FormRenderer";
import NotificationPopup from "../../common/NotificationPopup";
import {HasRole, hasRole, PasteToClipboard} from "../../common/Util";
import ConfirmButton from "../../components/ConfirmButton";
import StatusSwitch from "../../components/StatusSwitch";
import {useUser} from "../../Auth";
import {
    useRemoveContactMutation,
    useUpdateContactMutation,
} from "../../generated/graphql";
import CreateView from "../custom/CreateView";
import {omit} from 'lodash';
import {GridColDef, DataGridPro, GridRenderCellParams} from "@mui/x-data-grid-pro";

const CONTACT_REFETCH_QUERIES = ["getCustomer", "getOrder", "getSupplier", "getCustomerBriefList"];

export default ({ contacts, roles }) => {
    const user = useUser();
    const canEdit = !!hasRole(user, roles);
    const [updateContact] = useUpdateContactMutation();
    const [removeContact] = useRemoveContactMutation();
    const onSubmit = useCallback(
        async (contact, { resetForm }, setOpen) => {
            try {
                await updateContact({
                    variables: {
                        contact:omit(contact,["id"]),
                        id: contact.id,
                    },
                    refetchQueries: CONTACT_REFETCH_QUERIES,
                });
                NotificationPopup.success(`修改完成`);
                return contact;
            } catch (e) {
                NotificationPopup.error(
                    `Failed to update contact ${e.message}`
                );
                console.error(e);
            } finally {
                resetForm();
                setOpen(false);
            }
        },
        [updateContact]
    );
    const onRemove = (id) => {
        try {
            removeContact({
                variables: {
                    id: id,
                },
                refetchQueries: CONTACT_REFETCH_QUERIES,
            });
            NotificationPopup.success(`刪除完成`);
        } catch (e) {
            NotificationPopup.error(`刪除發生問題: ${e.message}`);
            console.error(e);
        }
    };
    const columns: GridColDef[] = [
        { field: "name", headerName: "姓名", width: 150 },
        { field: "phone", headerName: "電話", width: 150 },
        { field: "email", headerName: "Email", width: 250 },
        {
            field: "disabled",
            headerName: "狀態",
            width: 120,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <StatusSwitch
                        key={params.row.id}
                        enabled={!params.row.disabled}
                        disabled={!canEdit}
                        onChange={async (enabled) => {
                            try {
                                await updateContact({
                                    variables: {
                                        id: params.row.id,
                                        contact: {
                                            disabled: !enabled,
                                        },
                                    },
                                    refetchQueries: CONTACT_REFETCH_QUERIES,
                                });
                                NotificationPopup.success(`已${enabled ? "啟用" : "停用"}`);
                            } catch (e) {
                                NotificationPopup.error(`修改狀態發生問題: ${e.message}`);
                                throw e;
                            }
                        }}
                    />
                );
            },
        },
        {
            field: "test",
            headerName: "動作",
            width: 200,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <div style={{ display: "flex" }}>

                        <HasRole roles={roles}>
                        <CreateView
                            title={"修改"}
                            columns={[
                                {
                                    name: "id",
                                    label: "ID",
                                    value: params.row.id,
                                    type: InputType.hidden,
                                },
                                {
                                    name: "name",
                                    label: "姓名",
                                    value: params.row.name,
                                },
                                {
                                    name: "phone",
                                    label: "電話",
                                    value: params.row.phone,
                                },
                                {
                                    name: "email",
                                    label: "Email",
                                    value: params.row.email,
                                },
                            ]}
                            onSubmit={onSubmit}
                            submitTitle={"修改"}
                            queryEntity={"contact"}
                        />
                        </HasRole>
                        &ensp;

                        <HasRole roles={roles}>
                        <ConfirmButton
                            onConfirm={() => {
                                onRemove(params.row.id);
                            }}
                            queryEntity={"contact"}
                            queryAction={"刪除聯絡人"}
                            entityId={params.row.id}
                            queryMessage={`名稱: ${params.row.name}`}
                        >
                            刪除
                        </ConfirmButton>
                        </HasRole>
                    </div>
                );
            },
        },
    ];
    return (
        <Box width={"100%"} style={{ marginTop: 20, height: 200 }}>
            <DataGridPro
                columns={columns}
                rows={contacts}
                hideFooter={true}
                autoHeight={true}
                localeText={DataGridLocale}
                onCellClick={PasteToClipboard}
            />
        </Box>
    );
};
